<script setup>
import EyeIcon from '@iconify-vue/ph/eye';
import InfoIcon from '@iconify-vue/ph/info';
import MagnifyingGlassMinusIcon from '@iconify-vue/ph/magnifying-glass-minus';
import MagnifyingGlassPlusIcon from '@iconify-vue/ph/magnifying-glass-plus';
import XIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
    VisuallyHidden,
} from 'reka-ui';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, ref, watch } from 'vue';

const model = defineModel();

const {
    fieldId,
    fieldName,
    label,
    placeholder = '',
    legend = null,
    type = 'text',
    min = null,
    max = null,
    step = null,
    disabled = false,
    readonly = false,
    required = false,
    accept = '',
    multiple = false,
    tooltipIcon = InfoIcon,
    tooltipContent = null,
    tooltipColor = null,
    tooltipPlacement = 'right',
    inlineLabel = false,
    size = 'medium',
    presetColors = null,
    withoutMargin = false,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: false,
    },
    placeholder: {
        type: String,
        required: false,
    },
    legend: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    min: {
        type: [Number, String],
        required: false,
    },
    max: {
        type: [Number, String],
        required: false,
    },
    step: {
        type: [Number, String],
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    readonly: {
        type: Boolean,
        required: false,
    },
    required: {
        type: Boolean,
        required: false,
    },
    accept: {
        type: String,
        required: false,
    },
    multiple: {
        type: Boolean,
        required: false,
    },
    tooltipIcon: {
        type: [Function, Object],
        required: false,
    },
    tooltipContent: {
        type: [String, Object],
        required: false,
    },
    tooltipColor: {
        type: String,
        required: false,
    },
    tooltipPlacement: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    inlineLabel: {
        type: Boolean,
        required: false,
    },
    presetColors: {
        type: Array,
        required: false,
    },
    withoutMargin: {
        type: Boolean,
        required: false,
    },
});

const isTooltipOpen = ref(false);

const hasTooltipContent = computed(
    () =>
        tooltipContent !== null &&
        tooltipContent !== '' &&
        typeof tooltipContent !== 'undefined',
);

const fileModalIsOpen = ref(false);

function setFileModalIsOpen(value) {
    fileModalIsOpen.value = value;
}

function focusInput() {
    document.querySelector('#' + fieldId).focus();
}

function getInput() {
    return document.querySelector('#' + fieldId);
}

function getImageUrl(file) {
    return URL.createObjectURL(file);
}

const hasExistingImage = computed(() => {
    if (typeof model.value !== 'string' || !model.value) return false;
    const input = getInput();
    return !(input && input.files && input.files.length > 0);
});

const isImageAccept = computed(() => accept.includes('image'));

const singleFilePreviewUrl = ref(null);

function onFileChange() {
    const input = getInput();

    if (
        !multiple &&
        isImageAccept.value &&
        input?.files?.length > 0 &&
        input.files[0].type.startsWith('image/')
    ) {
        singleFilePreviewUrl.value = URL.createObjectURL(input.files[0]);
    } else {
        singleFilePreviewUrl.value = null;
    }
}

const lightboxOpen = ref(false);

const lightboxScale = ref(1);

const lightboxTranslateX = ref(0);

const lightboxTranslateY = ref(0);

const lightboxDragging = ref(false);

const lightboxDragStart = ref({ x: 0, y: 0 });

const lightboxSrc = computed(() => {
    if (singleFilePreviewUrl.value) return singleFilePreviewUrl.value;
    if (hasExistingImage.value) return model.value;
    return null;
});

function resetLightboxZoom() {
    lightboxScale.value = 1;
    lightboxTranslateX.value = 0;
    lightboxTranslateY.value = 0;
}

function lightboxZoomIn() {
    lightboxScale.value = Math.min(5, lightboxScale.value + 0.5);
    if (lightboxScale.value === 1) {
        lightboxTranslateX.value = 0;
        lightboxTranslateY.value = 0;
    }
}

function lightboxZoomOut() {
    lightboxScale.value = Math.max(1, lightboxScale.value - 0.5);
    if (lightboxScale.value === 1) {
        lightboxTranslateX.value = 0;
        lightboxTranslateY.value = 0;
    }
}

function onLightboxWheel(e) {
    e.preventDefault();
    if (e.deltaY < 0) lightboxZoomIn();
    else lightboxZoomOut();
}

function onLightboxPointerDown(e) {
    if (lightboxScale.value <= 1) return;
    lightboxDragging.value = true;
    lightboxDragStart.value = {
        x: e.clientX - lightboxTranslateX.value,
        y: e.clientY - lightboxTranslateY.value,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
}

function onLightboxPointerMove(e) {
    if (!lightboxDragging.value) return;
    lightboxTranslateX.value = e.clientX - lightboxDragStart.value.x;
    lightboxTranslateY.value = e.clientY - lightboxDragStart.value.y;
}

function onLightboxPointerUp() {
    lightboxDragging.value = false;
}

watch(lightboxOpen, (open) => {
    if (!open) resetLightboxZoom();
});
</script>

<template>
    <div
        class="flex"
        :class="{
            hidden: type === 'hidden',
            'items-center': inlineLabel,
            'flex-col': !inlineLabel,
            'mb-4': !withoutMargin,
        }"
    >
        <label
            :for="fieldName"
            class="inline-flex items-center gap-1"
            :class="{
                'mr-2': inlineLabel,
                'mb-2': !inlineLabel,
                'text-sm': size === 'small',
            }"
        >
            {{ label }}

            <sup v-if="required" class="text-red-500">*</sup>

            <span
                v-if="hasTooltipContent"
                class="relative inline-flex items-center justify-center"
            >
                <TooltipProvider>
                    <TooltipRoot
                        v-model:open="isTooltipOpen"
                        :delay-duration="0"
                    >
                        <TooltipTrigger as-child>
                            <button type="button">
                                <component
                                    :is="tooltipIcon"
                                    class="size-5 cursor-help"
                                    :class="{
                                        'text-indigo-600':
                                            tooltipColor === null,
                                        'text-red-500': tooltipColor === 'red',
                                        'text-orange-500':
                                            tooltipColor === 'orange',
                                        'text-yellow-500':
                                            tooltipColor === 'yellow',
                                        'text-green-500':
                                            tooltipColor === 'green',
                                    }"
                                />
                            </button>
                        </TooltipTrigger>

                        <TooltipPortal>
                            <AnimatePresence>
                                <TooltipContent
                                    v-if="isTooltipOpen"
                                    as-child
                                    force-mount
                                    :side="tooltipPlacement"
                                    :side-offset="5"
                                >
                                    <Motion
                                        :initial="{ opacity: 0, scale: 0.96 }"
                                        :animate="{ opacity: 1, scale: 1 }"
                                        :exit="{ opacity: 0, scale: 0.96 }"
                                        :transition="{ duration: 0.15 }"
                                        class="wysiwyg z-50 max-h-32 min-w-32 overflow-y-auto rounded-sm bg-white px-2 py-1 drop-shadow-sm"
                                    >
                                        <div
                                            v-if="
                                                typeof tooltipContent ===
                                                'string'
                                            "
                                            v-html="tooltipContent"
                                        ></div>
                                        <component
                                            v-else
                                            :is="tooltipContent"
                                        />
                                    </Motion>
                                </TooltipContent>
                            </AnimatePresence>
                        </TooltipPortal>
                    </TooltipRoot>
                </TooltipProvider>
            </span>
        </label>

        <div class="flex">
            <div
                v-if="$slots.inputPrefix"
                class="h-[50px] rounded-sm rounded-r-none border border-r-0 border-gray-300 py-3 pl-5 whitespace-nowrap"
                @click="focusInput"
            >
                <slot name="inputPrefix"></slot>
            </div>

            <Field
                v-model="model"
                :id="fieldId"
                :name="fieldName"
                :type="type"
                :placeholder="placeholder"
                :min="min ? min : ''"
                :maxlength="max ? max : ''"
                :step="step ? step : ''"
                :disabled="disabled"
                :readonly="readonly"
                :accept="accept"
                :multiple="multiple"
                class="w-full rounded-sm border-gray-300 bg-white ring-1 ring-transparent duration-150 file:mr-3 file:h-full file:cursor-pointer file:rounded-sm file:border-0 file:bg-indigo-600 file:px-3 file:py-1 file:text-sm file:text-white file:shadow-none placeholder:text-gray-400 hover:file:bg-indigo-950 focus:ring-gray-300 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-100"
                :class="{
                    hidden: type === 'file',
                    border: type !== 'file',
                    'rounded-l-none border-l-0 pl-1': $slots.inputPrefix,
                    'rounded-r-none': $slots.inputSuffix,
                    'h-[50px] px-5 py-3': size === 'medium',
                    'h-[28px] px-3 py-1 text-sm': size === 'small',
                    'cursor-pointer hover:bg-gray-100': type === 'color',
                }"
                @change="type === 'file' ? onFileChange() : null"
                @paste="$emit('paste', $event)"
                :list="
                    type === 'color' && presetColors
                        ? fieldId + '-preset-colors'
                        : null
                "
            />

            <div
                v-if="$slots.inputSuffix"
                class="flex items-center rounded-sm rounded-l-none border border-l-0 border-gray-300 bg-white px-2 whitespace-nowrap"
                :class="{
                    'h-[50px]': size === 'medium',
                    'h-[28px]': size === 'small',
                }"
            >
                <slot name="inputSuffix"></slot>
            </div>

            <datalist
                v-if="type === 'color' && presetColors"
                :id="fieldId + '-preset-colors'"
            >
                <option
                    v-for="(presetColor, index) in presetColors"
                    :key="index"
                    :value="presetColor"
                />
            </datalist>

            <div v-if="type === 'file'" class="flex flex-col gap-2">
                <img
                    v-if="hasExistingImage"
                    :src="model"
                    alt="Image actuelle"
                    class="size-60 cursor-zoom-in rounded-sm border border-gray-300 object-contain"
                    @click="lightboxOpen = true"
                />

                <img
                    v-if="singleFilePreviewUrl && !multiple"
                    :src="singleFilePreviewUrl"
                    alt="Aperçu de l'image"
                    class="size-60 cursor-zoom-in rounded-sm border border-gray-300 object-contain"
                    @click="lightboxOpen = true"
                />

                <div class="flex items-center gap-2">
                    <div
                        @click="() => getInput().click()"
                        class="cursor-pointer rounded-sm border border-dashed border-gray-300 bg-white text-center duration-150 hover:bg-gray-100"
                        :class="{
                            'h-[50px] px-5 py-3': size === 'medium',
                            'h-[28px] px-3 py-1 text-sm': size === 'small',
                        }"
                    >
                        <span
                            v-if="
                                getInput() &&
                                getInput().files &&
                                getInput().files.length > 0
                            "
                            class="font-medium"
                        >
                            {{ getInput().files.length }} fichier sélectionné(s)
                        </span>

                        <template v-else> Aucun fichier sélectionné </template>
                    </div>

                    <ActionButton
                        v-if="multiple"
                        :callback="() => setFileModalIsOpen(true)"
                        class="aspect-square"
                        :class="{
                            'h-[50px]': size === 'medium',
                            'h-[28px]': size === 'small',
                        }"
                        size="small"
                        :icon="EyeIcon"
                        :disabled="
                            !(
                                getInput() &&
                                getInput().files &&
                                getInput().files.length > 0
                            )
                        "
                        square
                    />

                    <DialogModal
                        v-if="multiple"
                        v-model="fileModalIsOpen"
                        size="large"
                    >
                        <template #title>
                            Détails des fichiers sélectionnés
                        </template>

                        <template #content>
                            <ul class="flex flex-col gap-4">
                                <li
                                    v-for="(file, index) in getInput().files"
                                    :key="index"
                                    class="flex flex-col items-start gap-1"
                                >
                                    <span class="font-medium">
                                        {{ file.name }} ({{
                                            (file.size / 1024).toFixed(2)
                                        }}
                                        KB)
                                    </span>

                                    <img
                                        v-if="file.type.startsWith('image/')"
                                        :src="getImageUrl(file)"
                                        alt="Aperçu de l'image"
                                        class="max-h-40 rounded-sm border border-gray-300 object-contain"
                                    />
                                </li>
                            </ul>
                        </template>
                    </DialogModal>
                </div>
            </div>
        </div>

        <em v-if="legend" class="mt-1 text-xs text-gray-400">
            {{ legend }}
        </em>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />

        <DialogRoot v-model:open="lightboxOpen">
            <DialogPortal forceMount>
                <AnimatePresence>
                    <Motion
                        v-if="lightboxOpen"
                        key="overlay"
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :exit="{ opacity: 0 }"
                        :transition="{ duration: 0.2 }"
                        class="fixed inset-0 z-50"
                    >
                        <DialogOverlay
                            forceMount
                            class="size-full bg-black/80"
                        />
                    </Motion>

                    <Motion
                        v-if="lightboxOpen"
                        key="content"
                        :initial="{ opacity: 0, scale: 0.96 }"
                        :animate="{ opacity: 1, scale: 1 }"
                        :exit="{ opacity: 0, scale: 0.96 }"
                        :transition="{
                            type: 'spring',
                            duration: 0.3,
                            bounce: 0.1,
                        }"
                        class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
                    >
                        <DialogContent
                            forceMount
                            class="pointer-events-auto relative max-h-[90vh] max-w-[90vw] focus:outline-none"
                            :aria-describedby="undefined"
                        >
                            <VisuallyHidden>
                                <DialogTitle />
                            </VisuallyHidden>

                            <div
                                class="absolute -top-12 right-0 left-0 flex items-center justify-between"
                            >
                                <div class="flex gap-2">
                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="lightboxScale <= 1"
                                        title="Dézoomer"
                                        @click="lightboxZoomOut"
                                    >
                                        <MagnifyingGlassMinusIcon
                                            class="size-5"
                                        />
                                    </button>

                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="lightboxScale >= 5"
                                        title="Zoomer"
                                        @click="lightboxZoomIn"
                                    >
                                        <MagnifyingGlassPlusIcon
                                            class="size-5"
                                        />
                                    </button>
                                </div>

                                <DialogClose
                                    class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 hover:bg-indigo-700"
                                >
                                    <XIcon class="size-5" />
                                </DialogClose>
                            </div>

                            <div
                                class="overflow-hidden rounded-lg shadow-lg"
                                @wheel="onLightboxWheel"
                                @dblclick="resetLightboxZoom"
                            >
                                <img
                                    :src="lightboxSrc"
                                    alt="Aperçu"
                                    :style="{
                                        transform: `scale(${lightboxScale}) translate(${lightboxTranslateX / lightboxScale}px, ${lightboxTranslateY / lightboxScale}px)`,
                                    }"
                                    class="max-h-[85vh] max-w-[85vw] object-contain transition-transform duration-150"
                                    :class="{
                                        'cursor-grab':
                                            lightboxScale > 1 &&
                                            !lightboxDragging,
                                        'cursor-grabbing': lightboxDragging,
                                        '!duration-0': lightboxDragging,
                                    }"
                                    draggable="false"
                                    @pointerdown="onLightboxPointerDown"
                                    @pointermove="onLightboxPointerMove"
                                    @pointerup="onLightboxPointerUp"
                                />
                            </div>
                        </DialogContent>
                    </Motion>
                </AnimatePresence>
            </DialogPortal>
        </DialogRoot>
    </div>
</template>
